// A location's last drawer activity on the staff Accounts list (legacy library/locationActivity.ts): the recency band
// that colours its chip and drives the "Last drawer activity" filter. Legacy formatted the tooltip in the browser's zone;
// here it is the location's wall clock (B7/B8/B16).
export type ActivityBucket = 'active_today' | 'active_recent' | 'at_risk' | 'seasonal' | 'long_gone';
export type ActivityVariant = 'green' | 'blue' | 'red' | 'orange' | 'gray';

interface DrawerActivity {
  readonly bucket: ActivityBucket;
  readonly variant: ActivityVariant;
  readonly label: string;
}

/** The filter's options, most to least active (legacy DRAWER_ACTIVITY_FILTERS). */
export const DRAWER_ACTIVITY_FILTERS: readonly { readonly value: ActivityBucket; readonly label: string }[] = [
  { value: 'active_today', label: 'Active — today' },
  { value: 'active_recent', label: 'Active — last few days' },
  { value: 'at_risk', label: 'At risk — 3 to 30 days' },
  { value: 'seasonal', label: 'Seasonal — 30 to 90 days' },
  { value: 'long_gone', label: 'Long gone — 90+ days' },
];

const HOUR = 3_600_000;

const relative = (hours: number, days: number): string => {
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

/**
 * Under 24 hours: active today (green); under 3 days: active recently (blue); under 30 days: at risk (red); up to 90
 * days: seasonal (orange); beyond, or never: long gone (gray, "Never" when no drawer has opened). Whole hours and days
 * elapsed, as moment's `diff`.
 */
export function drawerActivity(openedAt: string | null | undefined, now: number): DrawerActivity {
  if (openedAt === null || openedAt === undefined) return { bucket: 'long_gone', variant: 'gray', label: 'Never' };
  const elapsed = now - Date.parse(openedAt);
  const hours = Math.floor(elapsed / HOUR);
  const days = Math.floor(elapsed / (24 * HOUR));
  const [bucket, variant]: [ActivityBucket, ActivityVariant] =
    hours < 24 ? ['active_today', 'green']
      : days < 3 ? ['active_recent', 'blue']
        : days < 30 ? ['at_risk', 'red']
          : days <= 90 ? ['seasonal', 'orange']
            : ['long_gone', 'gray'];
  return { bucket, variant, label: relative(hours, days) };
}

/** "Last drawer opened Oct 12, 2026 at 9:30 AM" on the location's clock, or legacy's line for never. */
export function activityTooltip(openedAt: string | null | undefined, timezone: string): string {
  if (openedAt === null || openedAt === undefined) return 'No drawer has been opened at this location';
  const at = new Date(openedAt);
  const day = new Intl.DateTimeFormat('en-US', { timeZone: timezone, month: 'short', day: 'numeric', year: 'numeric' }).format(at);
  const time = new Intl.DateTimeFormat('en-US', { timeZone: timezone, hour: 'numeric', minute: '2-digit' }).format(at);
  return `Last drawer opened ${day} at ${time}`;
}
